import {GroupedVirtuoso} from 'react-virtuoso';
import {
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonNote,
    IonPage,
    IonProgressBar,
    IonTitle,
    IonToolbar,
    isPlatform
} from '@ionic/react';
import {IonInfiniteScrollCustomEvent} from '@ionic/core/dist/types/components';
import React, {memo, useCallback, useMemo} from 'react';
import styles from './Log.module.css';
import {useAlarmPrinterLogQuery} from '../query/alarmPrinterLogQuery';
import {useGatewayConnectionStateQuery} from '../query/gatewayConnectionStateQuery';

const AlarmPrinterLog: React.FC = () => {
    const logQuery = useAlarmPrinterLogQuery();
    const {isConnecting} = useGatewayConnectionStateQuery();
    const logRows = useMemo(() => logQuery.data?.pages.flat() ?? [], [logQuery.data]);

    const dateFormat = useMemo(() => Intl.DateTimeFormat('default', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    }), []);

    const timeFormat = useMemo(() => Intl.DateTimeFormat('default', {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
    }), []);

    const formatDate = useCallback((timestamp: string | null) => {
        if (!timestamp) {
            return 'Unknown date';
        }

        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? 'Unknown date' : dateFormat.format(date);
    }, [dateFormat]);

    const formatTimestamp = useCallback((timestamp: string | null) => {
        if (!timestamp) {
            return '';
        }

        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? '' : timeFormat.format(date);
    }, [timeFormat]);

    const {logGroupCounts, logGroups} = useMemo(() => {
        const logGroupCounts: number[] = [];
        const logGroups: string[] = [];
        let lastDate: string | undefined;

        for (const row of logRows) {
            const date = formatDate(row.timestamp);

            if (date !== lastDate) {
                lastDate = date;
                logGroups.push(date);
                logGroupCounts.push(0);
            }

            logGroupCounts[logGroupCounts.length - 1]++;
        }

        return {logGroupCounts, logGroups};
    }, [formatDate, logRows]);

    const loadMore = useCallback(async (event: IonInfiniteScrollCustomEvent<void>) => {
        try {
            await logQuery.fetchNextPage();
        } finally {
            await event.target.complete();
        }
    }, [logQuery]);

    const Footer = useCallback(() => (
        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!logQuery.hasNextPage} threshold="100px">
            <IonInfiniteScrollContent/>
        </IonInfiniteScroll>
    ), [loadMore, logQuery.hasNextPage]);

    return (
        <IonPage>
            <IonHeader translucent={isPlatform('capacitor')} collapse="fade">
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/access/alarm"/>
                    </IonButtons>
                    <IonTitle>Alarm Printer Log</IonTitle>
                    {(logQuery.isFetching || isConnecting) && <IonProgressBar type="indeterminate"/>}
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen={true} scrollY={false}>
                <GroupedVirtuoso
                    className="ion-content-scroll-host"
                    groupCounts={logGroupCounts}
                    itemContent={(index: number) => {
                        const row = logRows[index];

                        return (
                            <IonItem className={styles.listItem}>
                                <IonLabel class="ion-text-wrap">
                                    <IonNote>{formatTimestamp(row.timestamp)}</IonNote>
                                    <h4>{row.message}</h4>
                                </IonLabel>
                            </IonItem>
                        );
                    }}
                    groupContent={(index: number) => (
                        <IonItemDivider>{logGroups[index]}</IonItemDivider>
                    )}
                    components={{Footer}}
                />
            </IonContent>
        </IonPage>
    );
};

export default memo(AlarmPrinterLog);
